import { makeObservable, observable, runInAction } from "mobx";
import { registerFormStepParam } from "auth/authPages";
import { getUrlParam } from "helpers/url";
import { type IAnalyticsManager } from "services/analytics/analyticsManager";
import { type IIncorporationStore } from "services/incorporation/incorporationStore";
import { IncorporationStatus } from "services/incorporation/incorporationTypes";
import { type IIssuesStore } from "services/issues/issuesStore";
import type { IUserStore } from "services/user/userStore";
import { type FormSubmitResult } from "types/form";
import { type LoadableState } from "types/loadableState";
import { type RegisterFormStepsStoreBase } from "./registerFormStepsStoreBase";

type RegisterFormStoreState = LoadableState<"done">;

export abstract class RegisterFormStoreBase<T> {
  protected abstract _init(): Promise<void>;
  protected abstract _getRelevantStep(): T;
  protected abstract _updateStepData(data?: unknown, entity?: unknown): Promise<FormSubmitResult>;

  private _state: RegisterFormStoreState = "loading";

  protected constructor(
    protected readonly _stepsStore: RegisterFormStepsStoreBase<T>,
    protected readonly _userStore: IUserStore,
    protected readonly _incorporationStore: IIncorporationStore,
    protected readonly _companyIssuesStore: IIssuesStore,
    protected readonly _analyticsManager: IAnalyticsManager,
  ) {
    makeObservable(this, {
      _state: observable,
    } as any);
  }

  protected set state(value: RegisterFormStoreState) {
    this._state = value;
  }

  get state(): RegisterFormStoreState {
    return this._state;
  }

  get showAlert(): boolean {
    return (
      this._incorporationStore.incorporation?.status === IncorporationStatus.Rejected &&
      this._companyIssuesStore.hasIssues() &&
      !this._stepsStore.isLastStep
    );
  }

  get hideBackButton(): boolean {
    return this._stepsStore.isFirstStep || this._isInRejectionFlow;
  }

  get hideTitle(): boolean {
    return this.getHideTitle();
  }

  protected getHideTitle(): boolean {
    return false;
  }

  protected getHideBackButton(): boolean {
    return this._stepsStore.isFirstStep || this._isInRejectionFlow;
  }

  private get _isInRejectionFlow(): boolean {
    return Boolean(this._incorporationStore.incorporation) || this._companyIssuesStore.hasIssues();
  }

  protected get isRejectedButAllIssuesResolved(): boolean {
    const { incorporation } = this._incorporationStore;
    return incorporation?.status === IncorporationStatus.Rejected && !this._companyIssuesStore.hasIssues();
  }

  async init(): Promise<void> {
    try {
      await this._init();

      runInAction(() => {
        this._setStep();

        this.state = "done";
      });
    } catch (error: unknown) {
      this.state = "error";

      this._analyticsManager.event("load-form-error");

      throw new Error(`Form couldn‘t load with the error: ${(error as Error).toString()}`);
    }
  }

  protected _onGoBack(): void {
    // Do nothing by default
  }

  async goBack(): Promise<void> {
    this._onGoBack();
    this._analyticsManager.event("click-back", {
      title: this._stepsStore.activeStep.title,
      type: this._stepsStore.activeStep.type,
      stepId: this._stepsStore.activeStepTestId,
    });
    this._stepsStore.goPreviousQuestion();
  }

  async onSubmit(data?: unknown, entity?: unknown): Promise<FormSubmitResult> {
    const result = await this._updateStepData(data, entity);
    if (result) {
      return result;
    }
    this._analyticsManager.event("click-next", {
      title: this._stepsStore.activeStep.title,
      type: this._stepsStore.activeStep.type,
      stepId: this._stepsStore.activeStepTestId,
    });
    await this._stepsStore.goNextQuestion();
  }

  protected async _createIncorporation(): Promise<void> {
    await this._incorporationStore.createIncorporation();

    this._analyticsManager.googleEvent("application_sent");
  }

  private _setStep(): void {
    if (this._incorporationStore.incorporation) {
      if (this._companyIssuesStore.skipConfirmScreen()) {
        this._stepsStore.setStep(this._stepsStore.completeStep);
        return;
      }
      const landingStep = this.isRejectedButAllIssuesResolved
        ? this._stepsStore.confirmStep
        : this._stepsStore.completeStep;

      this._stepsStore.setStep(landingStep);
      return;
    }

    const stepFromUrl = this._tryGetStepFromUrl();
    const step =
      stepFromUrl !== undefined && this._canResumeToStepFromUrl(stepFromUrl) ? stepFromUrl : this._getRelevantStep();
    this._stepsStore.setStep(step);
  }

  // Subclasses can veto a step requested via the URL (e.g. a stale deep link back to a
  // payment step after payment is settled) — the form then falls back to _getRelevantStep().
  protected _canResumeToStepFromUrl(_step: T): boolean {
    return true;
  }

  private _tryGetStepFromUrl(): T | undefined {
    const stepFromUrl = getUrlParam(registerFormStepParam) ?? "";
    return this._stepsStore.getStepByName(stepFromUrl);
  }
}
